import { Component } from '@theme/component';

/**
 * Customer reviews, drawn in the theme's own design from Judge.me's data.
 *
 * Judge.me's widget for this theme is a fixed "minimal header" layout (score, button, cards) with no
 * rating breakdown and no way to restyle its structure, so the summary card and the review cards
 * are built here from the same JSON the widget itself reads:
 *
 *   https://judge.me/reviews/reviews_for_widget?shop_domain=…&platform=shopify&product_id=…&page=…
 *
 * It is the public endpoint behind Judge.me's own widgets (open CORS, cached for 20 minutes). It is
 * not a documented API, so everything that goes wrong here (blocked, changed shape, offline) falls
 * back to Judge.me's own widget, which stays on the page and is simply hidden while this works.
 * Writing a review is Judge.me's form: the "Write a review" button clicks the button inside that
 * widget, so submissions, moderation and emails stay in Judge.me.
 *
 * @extends {Component<{ summary: HTMLElement, list: HTMLElement, more: HTMLButtonElement, status: HTMLElement }>}
 */
class OudhReviews extends Component {
  static perPage = 5;

  /** @type {number} */
  #page = 0;
  /** @type {number} */
  #totalPages = 1;
  #loading = false;

  connectedCallback() {
    super.connectedCallback();

    const start = () => this.#load(1);
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(start, { timeout: 2500 });
    } else {
      window.setTimeout(start, 800);
    }
  }

  more() {
    if (this.#page < this.#totalPages) this.#load(this.#page + 1);
  }

  /** Opens Judge.me's own review form by pressing the button in its widget. */
  write() {
    const button = this.querySelector('.oudh-reviews__judgeme .jm-action-buttons__button, .oudh-reviews__judgeme .jdgm-write-rev-link');
    if (button instanceof HTMLElement) {
      button.click();
      return;
    }
    // Judge.me's widget has not drawn yet: show it so the visitor can still reach the form.
    this.setAttribute('data-fallback', '');
  }

  /** @param {number} page */
  async #load(page) {
    if (this.#loading) return;
    this.#loading = true;

    const { productId, shop } = this.dataset;
    const url =
      'https://judge.me/reviews/reviews_for_widget' +
      `?url=${encodeURIComponent(shop ?? '')}&shop_domain=${encodeURIComponent(shop ?? '')}&platform=shopify` +
      `&per_page=${OudhReviews.perPage}&product_id=${encodeURIComponent(productId ?? '')}&page=${page}`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Judge.me answered ${response.status}`);
      const data = await response.json();
      if (!data || !Array.isArray(data.reviews)) throw new Error('Unexpected Judge.me response');

      this.#page = page;
      this.#totalPages = Number(data.pagination?.total_pages) || 1;
      if (page === 1) this.#renderSummary(data);
      this.#renderReviews(data.reviews);
      this.refs.more.hidden = this.#page >= this.#totalPages;
      this.setAttribute('data-ready', '');
      this.removeAttribute('data-fallback');
    } catch (error) {
      console.warn('[oudh-reviews] falling back to the Judge.me widget:', error);
      this.setAttribute('data-fallback', '');
    } finally {
      this.#loading = false;
    }
  }

  /**
   * @param {{ number_of_reviews?: number, average_rating?: string | number, histogram?: { rating: number, frequency: number, percentage: number }[] }} data
   */
  #renderSummary(data) {
    const { summary } = this.refs;
    const total = Number(data.number_of_reviews) || 0;
    const average = Number(data.average_rating) || 0;

    summary.replaceChildren();

    // With no reviews the card keeps its shape, so the page does not change layout the day the first
    // one arrives: the score reads "No reviews yet", the stars are grey and every bar is empty.
    const empty = total === 0;

    const score = element('div', 'oudh-reviews__score');
    score.append(
      element('span', `oudh-reviews__score-value${empty ? ' oudh-reviews__score-value--text' : ''}`, empty ? 'No reviews yet' : formatAverage(average)),
      element('span', 'oudh-reviews__score-max', '/5.0'),
      stars(empty ? 0 : average, 20),
      element('p', 'oudh-caption', empty ? 'Be the first to review this product' : `Based on ${total} ${total === 1 ? 'review' : 'reviews'}`)
    );

    const bars = element('div', 'oudh-reviews__bars');
    const rows = empty
      ? [5, 4, 3, 2, 1].map((rating) => ({ rating, frequency: 0, percentage: 0 }))
      : [...(data.histogram ?? [])].sort((a, b) => b.rating - a.rating);
    for (const row of rows) {
      const percent = Math.max(0, Math.min(100, Number(row.percentage) || 0));
      const fill = element('span', 'oudh-reviews__bar-fill');
      fill.style.width = `${percent}%`;
      const track = element('span', 'oudh-reviews__bar-track');
      track.append(fill);
      const bar = element('div', 'oudh-reviews__bar');
      bar.append(
        element('span', 'oudh-reviews__bar-label', `${row.rating} star`),
        track,
        element('span', 'oudh-reviews__bar-count', String(row.frequency ?? 0))
      );
      bars.append(bar);
    }

    const cta = element('div', 'oudh-reviews__cta');
    cta.append(this.#writeButton(true));
    summary.append(score, bars, cta);
  }

  /** @param {boolean} block Full width of its column (the summary card), or only as wide as its label. */
  #writeButton(block) {
    const button = element('button', `oudh-btn oudh-btn--secondary oudh-btn--square${block ? ' oudh-btn--block' : ''}`);
    button.type = 'button';
    button.setAttribute('on:click', '/write');
    button.append(icon('<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2"/><path d="M9 7h7"/><path d="M9 11h5"/>', 18), element('span', '', 'Write a review'));
    return button;
  }

  /** @param {any[]} reviews */
  #renderReviews(reviews) {
    const { list } = this.refs;
    for (const review of reviews) list.append(this.#card(review));
  }

  /** @param {any} review */
  #card(review) {
    const article = element('article', 'oudh-review');

    const who = element('div', 'oudh-review__who');
    who.append(stars(Number(review.rating) || 0, 15), element('span', 'oudh-review__author', review.reviewer_name || 'Customer'));
    if (review.verified_buyer) {
      const badge = element('span', 'oudh-review__verified');
      badge.append(icon('<circle cx="12" cy="12" r="9"/><path d="m8.5 12.5 2.5 2.5 4.5-5"/>', 12), document.createTextNode(' Verified buyer'));
      who.append(badge);
    }
    if (review.location) who.append(element('span', 'oudh-review__location', review.location));

    const head = element('header', 'oudh-review__head');
    head.append(who);
    const date = formatDate(review.created_at);
    if (date) {
      const time = element('time', 'oudh-review__date', date);
      time.dateTime = String(review.created_at ?? '');
      head.append(time);
    }
    article.append(head);

    if (review.title) article.append(element('p', 'oudh-review__title', review.title));

    const body = element('div', 'oudh-review__body');
    for (const text of paragraphs(review.body_html)) body.append(element('p', '', text));
    article.append(body);

    if (review.reply_content) {
      const reply = element('div', 'oudh-review__reply');
      reply.append(element('strong', '', `${review.shop_reply_name || 'Store'} replied`), element('p', '', String(review.reply_content)));
      article.append(reply);
    }

    if (review.product_variant_title) article.append(element('footer', 'oudh-review__foot', review.product_variant_title));

    return article;
  }
}

/* ----------------------------------------------------------------- helpers */

/**
 * @param {string} tag
 * @param {string} [className]
 * @param {string} [text]
 */
function element(tag, className = '', text = '') {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

/**
 * @param {string} paths
 * @param {number} size
 */
function icon(paths, size) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'oudh-icon');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.75');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML = paths; // static strings from this file, never user text
  return svg;
}

const STAR = 'M12 2.6 14.9 8.5 21.4 9.4 16.7 14 17.8 20.5 12 17.4 6.2 20.5 7.3 14 2.6 9.4 9.1 8.5z';

/**
 * Gold stars like snippets/oudh-stars.liquid, rounded to the nearest half.
 * @param {number} rating
 * @param {number} size
 */
function stars(rating, size) {
  const halves = Math.round(rating * 2);
  const wrapper = element('span', 'oudh-stars');
  const glyphs = element('span', 'oudh-stars__glyphs');
  glyphs.setAttribute('role', 'img');
  glyphs.setAttribute('aria-label', `${formatAverage(rating)} out of 5 stars`);

  for (let i = 1; i <= 5; i++) {
    const full = halves >= i * 2;
    const half = !full && halves === i * 2 - 1;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', String(size));
    svg.setAttribute('height', String(size));
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('class', 'oudh-icon');

    const path = (/** @type {string} */ fill) => {
      const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      p.setAttribute('d', STAR);
      p.setAttribute('fill', fill);
      return p;
    };
    // An empty star is the same glyph in the muted tone; a half star lays the gold left half over it.
    svg.append(path(full ? 'currentColor' : 'var(--oudh-surface-dim)'));
    if (half) {
      const id = `oudh-half-${Math.random().toString(36).slice(2, 8)}`;
      const clip = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
      clip.setAttribute('id', id);
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('width', '12');
      rect.setAttribute('height', '24');
      clip.append(rect);
      const left = path('currentColor');
      left.setAttribute('clip-path', `url(#${id})`);
      svg.append(clip, left);
    }
    glyphs.append(svg);
  }

  wrapper.append(glyphs);
  return wrapper;
}

/** "4.92", "4.9", "5.0" — two decimals, one trailing zero dropped. */
/** @param {number} value */
function formatAverage(value) {
  return value.toFixed(2).replace(/0$/, '');
}

/** @param {string | undefined} stamp */
function formatDate(stamp) {
  const date = stamp ? new Date(stamp) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Judge.me sends the body as HTML. Only its text is kept, one entry per paragraph.
 * @param {string | undefined} html
 * @returns {string[]}
 */
function paragraphs(html) {
  const doc = new DOMParser().parseFromString(html ?? '', 'text/html');
  const blocks = [...doc.body.querySelectorAll('p')].map((p) => p.textContent?.trim() ?? '').filter(Boolean);
  if (blocks.length) return blocks;
  const text = doc.body.textContent?.trim();
  return text ? [text] : [];
}

if (!customElements.get('oudh-reviews')) {
  customElements.define('oudh-reviews', OudhReviews);
}
